<template>
  <div class="invoice-payments-table table-component p-2">

    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :checkboxes="true" :search="tableData.search"
                   :enable-simple-search="false" export-vuex-module-name="invoicePayments" enable-frontend-export
                   export-permission-name="export_invoice"
                   @paramsChanged="getTableData" selectable filter-toggle-string="invoice-payments-table"
                   show-toggle-columns :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns">

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" class="mr-2"
                    v-permission="'change_invoice'">
          <b-dropdown-item @click="onGenerateBatchPaymentsClick(selectedItems)">
            {{ $t('Generate Batch Payments') }}
          </b-dropdown-item>
        </b-dropdown>

      </template>

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="invoice-payments-table">
          <invoice-payment-search/>
        </search-collapse>
      </template>

      <template slot="cell(invoice_no)" slot-scope="{item: invoicePayment}">
        <a href="javascript:void(0)" @click="onViewInvoicePdfClick(invoicePayment)">
          {{ invoicePayment.invoice.invoice_no }}
        </a>
        <action-buttons :delete-permission="'delete_invoice'"
                        :edit-permission="'change_invoice'"
                        :view="false"
                        :edit="!invoicePayment.financial_export && invoicePayment.invoice.status !== 'cancelled'"
                        :remove="!invoicePayment.financial_export && invoicePayment.invoice.status !== 'cancelled'"
                        @editClick="onEditClick(invoicePayment)"
                        @deleteClick="onRemoveClick(invoicePayment)"/>
      </template>

      <template slot="cell(invoice_type)" slot-scope="{item: invoicePayment}">
        <span class="badge border">
          {{ invoicePayment.invoice.type | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
      </template>

      <template slot="cell(sale_no)" slot-scope="{item: invoicePayment}">
        <router-link v-if="invoicePayment.invoice.auction && !enableContinuousAuction" v-permission="'view_auction'"
                     :to="{ name: 'sales.view', params: { id: invoicePayment.invoice.auction.uuid }}">
          {{ invoicePayment.invoice.auction.sale_no }}
        </router-link>
        <span v-else-if="invoicePayment.invoice.auction && enableContinuousAuction">
          {{ invoicePayment.invoice.auction.sale_no }}
        </span>
        <router-link v-else-if="invoicePayment.invoice.deposit_sale_no && !enableContinuousAuction"
                     v-permission="'view_auction'"
                     :to="{ name: 'sales.view', params: { id: invoicePayment.invoice.deposit_reservations[0].auction_uuid }}">
          {{ invoicePayment.invoice.deposit_sale_no }}
        </router-link>
        <live-sale-ref v-if="invoicePayment.invoice.auction && invoicePayment.invoice.auction.live_reference"
                       :href="invoicePayment.invoice.auction.live_url"
                       :label="invoicePayment.invoice.auction.live_reference"/>
      </template>

      <template slot="cell(customer)" slot-scope="{item: invoicePayment}">
        <template v-if="invoicePayment.invoice.customer" v-permission="'view_customer'">
          <router-link :to="{ name: 'customers.dashboard', params: { id: invoicePayment.invoice.customer.uuid }}">
            {{ invoicePayment.invoice.customer.display_name }}
          </router-link>
        </template>
      </template>

      <template slot="cell(company_name)" slot-scope="{item: invoicePayment}">
        <template v-if="invoicePayment.invoice.customer">
          {{ invoicePayment.invoice.customer.company_name }}
        </template>
      </template>

      <template slot="cell(customer_no)" slot-scope="{item: invoicePayment}">
        <template v-if="invoicePayment.invoice.customer" v-permission="'view_customer'">
          <router-link :to="{ name: 'customers.dashboard', params: { id: invoicePayment.invoice.customer.uuid }}">
            {{ invoicePayment.invoice.customer.customer_no }}
          </router-link>
        </template>
      </template>

      <template slot="cell(payment_type)" slot-scope="{item: invoicePayment}">
        <span class="badge border">
          {{ invoicePayment.payment.type | removeUnderscores | toUpperCaseFirst | translate}}
        </span>
      </template>

      <template slot="cell(method)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.method | removeUnderscores | toUpperCaseFirst | translate}}
      </template>

      <template slot="cell(amount)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.amount | asCurrency(invoicePayment.payment.currency.symbol) }}
      </template>

      <template slot="cell(date)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.date | toDate }}
      </template>

      <template slot="cell(bank_account)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.bank_account }}
      </template>

      <template slot="cell(reference_number)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.reference_number }}
      </template>

      <template slot="cell(description)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.description }}
      </template>

      <template slot="cell(comment)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.comment }}
      </template>

      <template slot="cell(batch_info)" slot-scope="{item: invoicePayment}">
        <div v-if="invoicePayment.payment.batch_payment">
          <div>
            <a href="javascript:void(0)"
               @click="onBatchEditClick(invoicePayment)">{{ invoicePayment.payment.batch_payment.payment_no }}</a>
          </div>
          <span v-as-status="invoicePayment.payment.batch_payment.status">
            {{ invoicePayment.payment.batch_payment.status | removeUnderscores | toUpperCaseFirst | translate}}
          </span>
        </div>
      </template>

      <template slot="cell(created_at)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.created_at | toDate }} by {{ invoicePayment.payment.created_by.display_name }}
      </template>

      <template slot="cell(updated_at)" slot-scope="{item: invoicePayment}">
        {{ invoicePayment.payment.updated_at | toDate }} by {{ invoicePayment.payment.updated_by.display_name }}
      </template>

      <template slot="cell(reconciled)" slot-scope="{item: invoicePayment}">
        <span class="badge bg-success text-white" v-if="invoicePayment.payment.reconciled">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>

      <template slot="cell(payment_history)" slot-scope="{item: invoicePayment}">
        <exports :item="{invoice_payments: [invoicePayment]}" current-field="invoice_payments"
                 :on-show-more-click="onShowPaymentMoreClick"/>
      </template>

      <template slot="cell(buyer_group_account)" slot-scope="{item: invoicePayment}" v-if="vendorBuyerGroupAccountsEnabled">
        <template v-if="invoicePayment.invoice.auction">
          {{ invoicePayment.invoice.auction.buyer_group_account }}
        </template>
      </template>

      <template slot="cell(exchange_rate)" slot-scope="{item: invoicePayment}">
        <template v-if="invoicePayment.payment">
          {{ invoicePayment.payment.exchange_rate }}
        </template>
      </template>

      <template slot="cell(branch)" slot-scope="{item: invoicePayment}">
        <template v-if="invoicePayment.invoice.branch">
          {{ invoicePayment.invoice.branch.name }}
        </template>
      </template>

      <template slot="cell(actions)" slot-scope="{item: invoicePayment}">
        <b-tooltip :target="`payment-undo-btn-${invoicePayment.uuid}`" :title="$t('Undo Overpayment Allocation')"/>
        <b-button :id="`payment-undo-btn-${invoicePayment.uuid}`" v-permission="'change_invoice'"
                  v-if="invoicePayment.payment.method === 'overpayment' && invoicePayment.payment.overpayment_source_uuid && !invoicePayment.payment.disable_overpayment_undo"
                  variant="danger" size="sm" class="mr-2" @click="onPaymentUndoClick(invoicePayment)">
          <font-awesome-icon icon="undo" class="icon"/>
        </b-button>
      </template>

    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {
    INVOICE_PAYMENTS_TABLE_NAME,
    SOURCE_INVOICE_PAYMENT_EXPORTS,
    SOURCE_INVOICE_PAYMENTS_TABLE
  } from "../constants";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import cloneDeep from "lodash/cloneDeep";
  import InvoicePaymentSearch from "@/modules/invoice-payments/InvoicePaymentSearch";
  import InvoicesService from "@/modules/invoices/InvoicesService";
  import Exports from "@/core/components/exports/Exports";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState, mapActions} = createNamespacedHelpers('invoicePayments');
  const {mapActions: invoicesMapActions} = createNamespacedHelpers('invoices');

  export default {
    name: "InvoicePaymentTable",
    components: {Exports, InvoicePaymentSearch, SearchCollapse, ActionButtons, ArtisioTable},
    data() {
      return {
        selectedItems: new Set(),
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
      }),
      ...mapStateGlobal({
        enableContinuousAuction: state => state.enableContinuousAuction,
        vendorBuyerGroupAccountsEnabled: state => state.vendorBuyerGroupAccountsEnabled,
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f =>
          f.isVisible && (this.vendorBuyerGroupAccountsEnabled || f.key !== 'buyer_group_account')
        );
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.vendorBuyerGroupAccountsEnabled ? [] : ['buyer_group_account'],
          tableName: `${INVOICE_PAYMENTS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'updateToggleColumns']),
      ...invoicesMapActions([
        'showInvoiceBatchPaymentModal',
        'getInvoiceBatchPaymentModalObject',
        'showPaymentModal',
        'updatePaymentModalObject',
        'showPaymentExportsModal',
      ]),

      async onEditClick(invoicePayment) {
        await InvoicesService.onEditPayment(this, invoicePayment, SOURCE_INVOICE_PAYMENTS_TABLE)
      },
      async onRemoveClick(invoicePayment) {
        await InvoicesService.onDeletePayment(this, invoicePayment, SOURCE_INVOICE_PAYMENTS_TABLE)
      },
      async onPaymentUndoClick(invoicePayment) {
        await InvoicesService.onOverpaymentUndo(this, invoicePayment, SOURCE_INVOICE_PAYMENTS_TABLE)
      },
      async onGenerateBatchPaymentsClick() {
        this.showInvoiceBatchPaymentModal({
          source: SOURCE_INVOICE_PAYMENTS_TABLE
        })
      },
      async onBatchEditClick(invoicePayment) {
        this.showInvoiceBatchPaymentModal({
          source: SOURCE_INVOICE_PAYMENTS_TABLE
        })
        this.getInvoiceBatchPaymentModalObject(invoicePayment.payment.batch_payment.uuid)
      },
      onViewInvoicePdfClick(invoicePayment) {
        InvoicesService.viewInvoicePdf(this, invoicePayment.invoice, true)
      },
      onShowPaymentMoreClick(item) {
        this.showPaymentExportsModal({
          invoice: item,
          source: SOURCE_INVOICE_PAYMENT_EXPORTS
        })
      },
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
    }
  }
</style>

